import { useState, useCallback, useMemo } from 'react';
import type { BeadTechnique } from '@bransoletki/shared';
import { BEAD_DIMENSIONS } from '@bransoletki/shared';

interface NewProjectDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onCreate: (config: {
    name: string;
    technique: BeadTechnique;
    width: number;
    height: number;
    beadSize: string;
  }) => void;
}

const TECHNIQUES: readonly { value: BeadTechnique; label: string; desc: string }[] = [
  { value: 'loom', label: 'Loom', desc: 'Prostokątna siatka' },
  { value: 'peyote', label: 'Peyote', desc: 'Przesunięte wiersze' },
  { value: 'brick', label: 'Brick Stitch', desc: 'Przesunięte kolumny' },
];

const BEAD_SIZES: readonly { value: string; label: string }[] = [
  { value: '11/0', label: '11/0 (1.6×1.3mm)' },
  { value: '15/0', label: '15/0 (1.3×1.0mm)' },
  { value: '8/0', label: '8/0 (2.1×2.0mm)' },
];

type DimensionMode = 'beads' | 'cm';

export function NewProjectDialog({ isOpen, onClose, onCreate }: NewProjectDialogProps) {
  const [name, setName] = useState('Nowy projekt');
  const [technique, setTechnique] = useState<BeadTechnique>('loom');
  const [beadSize, setBeadSize] = useState('11/0');
  const [width, setWidth] = useState(20);
  const [height, setHeight] = useState(30);
  const [lengthCm, setLengthCm] = useState(4);
  const [heightMode, setHeightMode] = useState<DimensionMode>('cm');

  const dims = BEAD_DIMENSIONS[beadSize] ?? [1.6, 1.3];
  const beadW = dims[0];
  const beadH = dims[1];

  // Compute effective height in beads from cm
  const effectiveHeight = heightMode === 'cm'
    ? Math.max(1, Math.round((lengthCm * 10) / beadH))
    : height;

  // Live preview of physical size
  const preview = useMemo(() => {
    const h = effectiveHeight;
    const physW = Math.round(width * beadW * 10) / 10;
    const physH = Math.round(h * beadH * 10) / 10;
    return { physW, physH, heightBeads: h };
  }, [width, effectiveHeight, beadW, beadH]);

  const handleCreate = useCallback(() => {
    const clampedW = Math.max(1, Math.min(500, width));
    const clampedH = Math.max(1, Math.min(500, effectiveHeight));
    onCreate({
      name: name.trim() || 'Nowy projekt',
      technique,
      width: clampedW,
      height: clampedH,
      beadSize,
    });
  }, [name, technique, width, effectiveHeight, beadSize, onCreate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Nowy projekt</h2>

        {/* Name */}
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Nazwa</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="selectable w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </label>

        {/* Technique */}
        <div className="mb-3">
          <span className="mb-1 block text-xs font-medium text-gray-600">Technika</span>
          <div className="flex gap-2">
            {TECHNIQUES.map((t) => (
              <button
                key={t.value}
                onClick={() => setTechnique(t.value)}
                className={`flex-1 rounded-lg border-2 px-2 py-2 text-center transition-colors ${
                  technique === t.value
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="text-sm font-medium">{t.label}</div>
                <div className="text-[10px] text-gray-400">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Bead size */}
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Rozmiar koralika</span>
          <select
            value={beadSize}
            onChange={(e) => setBeadSize(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {BEAD_SIZES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>

        {/* Width (always in beads) */}
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-medium text-gray-600">Szerokość (koraliki)</span>
          <input
            type="number"
            value={width}
            onChange={(e) => setWidth(parseInt(e.target.value, 10) || 1)}
            min={1}
            max={500}
            className="selectable w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </label>

        {/* Height: toggle between cm and beads */}
        <div className="mb-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">Długość</span>
            <div className="flex rounded-md border border-gray-300 text-[10px]">
              <button
                onClick={() => setHeightMode('cm')}
                className={`px-2 py-0.5 ${heightMode === 'cm' ? 'bg-primary-600 text-white' : 'text-gray-500'}`}
              >
                cm
              </button>
              <button
                onClick={() => { setHeightMode('beads'); setHeight(effectiveHeight); }}
                className={`px-2 py-0.5 ${heightMode === 'beads' ? 'bg-primary-600 text-white' : 'text-gray-500'}`}
              >
                rzędy
              </button>
            </div>
          </div>
          {heightMode === 'cm' ? (
            <input
              type="number"
              value={lengthCm}
              onChange={(e) => setLengthCm(parseFloat(e.target.value) || 1)}
              min={0.5}
              max={100}
              step={0.5}
              className="selectable w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          ) : (
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(parseInt(e.target.value, 10) || 1)}
              min={1}
              max={500}
              className="selectable w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          )}
        </div>

        {/* Live preview */}
        <div className="mb-4 rounded-lg bg-gray-50 px-3 py-2 text-center">
          <div className="text-xs text-gray-500">
            {width} × {preview.heightBeads} koralików
          </div>
          <div className="text-sm font-semibold text-gray-700">
            ≈ {preview.physW} × {preview.physH} mm
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleCreate}
            className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            Utwórz
          </button>
        </div>
      </div>
    </div>
  );
}
